import { useState, useCallback, useRef, useEffect } from 'react';

// Calculate Optimal Recognition Point (ORP) position
// Usually around 35% from the start of the word
export function calculateORP(word) {
  if (!word || word.length === 0) return 0;
  if (word.length === 1) return 0;
  if (word.length <= 3) return 1;
  return Math.floor(word.length * 0.35);
}

// Split word into parts: before ORP, ORP character, after ORP
export function splitWordByORP(word) {
  if (!word) return { before: '', orp: '', after: '' };
  const orpIndex = calculateORP(word);
  return {
    before: word.slice(0, orpIndex),
    orp: word[orpIndex] || '',
    after: word.slice(orpIndex + 1)
  };
}

// Calculate delay multiplier based on word characteristics
export function calculateDelayMultiplier(word, settings = {}) {
  let multiplier = 1;
  
  // Longer words get more time
  if (word.length > 8) multiplier += 0.3;
  else if (word.length > 6) multiplier += 0.15;
  
  // Punctuation pauses
  if (settings.pauseOnPunctuation !== false) {
    if (word.endsWith('.') || word.endsWith('!') || word.endsWith('?')) {
      multiplier += 0.8; // Longer pause for sentence end
    } else if (word.endsWith(',') || word.endsWith(';') || word.endsWith(':')) {
      multiplier += 0.4; // Brief pause for clause
    }
  }
  
  return multiplier;
}

// Check if word is a common article
export function isArticle(word) {
  const articles = ['a', 'an', 'the'];
  return articles.includes(word.toLowerCase());
}

export function useRSVP(words = [], initialPosition = 0, settings = {}) {
  const [currentIndex, setCurrentIndex] = useState(initialPosition);
  const [isPlaying, setIsPlaying] = useState(false);
  const [wpm, setWpm] = useState(settings.wpm || 300);
  
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);
  const accumulatedTimeRef = useRef(0);
  
  const currentWord = words[currentIndex] || '';
  const progress = words.length > 0 ? (currentIndex / words.length) * 100 : 0;
  const wordsRemaining = words.length - currentIndex;
  const estimatedTimeRemaining = Math.ceil(wordsRemaining / wpm);
  
  // Calculate base interval from WPM
  const baseInterval = 60000 / wpm; // milliseconds per word
  
  const play = useCallback(() => {
    setIsPlaying(true);
  }, []);
  
  const pause = useCallback(() => {
    setIsPlaying(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);
  
  const toggle = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);
  
  const goToPosition = useCallback((position) => {
    const newPos = Math.max(0, Math.min(position, words.length - 1));
    setCurrentIndex(newPos);
    accumulatedTimeRef.current = 0;
  }, [words.length]);
  
  const skipForward = useCallback((amount = 10) => {
    goToPosition(currentIndex + amount);
  }, [currentIndex, goToPosition]);
  
  const skipBackward = useCallback((amount = 10) => {
    goToPosition(currentIndex - amount);
  }, [currentIndex, goToPosition]);
  
  const goToStart = useCallback(() => {
    goToPosition(0);
  }, [goToPosition]);
  
  const goToEnd = useCallback(() => {
    goToPosition(words.length - 1);
  }, [goToPosition, words.length]);
  
  // Animation loop using requestAnimationFrame for precision
  useEffect(() => {
    if (!isPlaying || words.length === 0) return;
    
    const animate = (timestamp) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
      }
      
      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;
      
      accumulatedTimeRef.current += deltaTime;
      
      // Get current word and calculate its display duration
      const word = words[currentIndex];
      const multiplier = calculateDelayMultiplier(word, { pauseOnPunctuation: settings.pauseOnPunctuation });
      const wordInterval = baseInterval * multiplier;
      
      // Check if we should skip articles
      const shouldSkip = settings.skipArticles && isArticle(word);
      const effectiveInterval = shouldSkip ? wordInterval * 0.3 : wordInterval;
      
      if (accumulatedTimeRef.current >= effectiveInterval) {
        accumulatedTimeRef.current = 0;
        
        setCurrentIndex(prev => {
          if (prev >= words.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }
      
      if (isPlaying) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    
    lastTimeRef.current = 0;
    accumulatedTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, words, currentIndex, baseInterval, settings.pauseOnPunctuation, settings.skipArticles]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  return {
    currentWord,
    currentIndex,
    isPlaying,
    wpm,
    setWpm,
    progress,
    wordsRemaining,
    estimatedTimeRemaining,
    totalWords: words.length,
    play,
    pause,
    toggle,
    goToPosition,
    skipForward,
    skipBackward,
    goToStart,
    goToEnd,
    setCurrentIndex
  };
}
